import { Injectable } from '@angular/core';
import { FirebaseApp, initializeApp } from '@firebase/app';
import {
  Auth,
  AuthProvider,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  User as FirebaseUser,
  getAuth,
  GoogleAuthProvider,
  linkWithPopup,
  OAuthProvider,
  signInWithCustomToken,
  signInWithPopup,
  signOut,
} from '@firebase/auth';
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import { environment } from 'shared/environments/environment';
import { AnalyticsService } from 'shared/src/services/analytics.service';
import { firebaseConfig } from './firebase-config';

export type AuthProviderType = 'facebook' | 'google' | 'apple';

// getIdToken() goes to the network when the cached token has expired, and nothing guarantees
// it settles — on a dead connection the promise can stay pending indefinitely. Auth must settle
// regardless: everything waiting on authInitialized$ (the route guards, and every intercepted
// request) would otherwise hang forever, including the public /credits pages that need no token
// at all. Past this point we publish without a token and let the 401 retry recover.
const INITIAL_TOKEN_TIMEOUT_MS = 10_000;

//TODO: move all the analytics calls from here

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly app: FirebaseApp;

  private auth: Auth;

  private userSubject = new BehaviorSubject<FirebaseUser | null>(null);
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private authInitializedSubject = new BehaviorSubject<boolean>(false);

  authInitialized$ = this.authInitializedSubject.asObservable();
  user$: Observable<FirebaseUser | null> = this.userSubject.asObservable();
  token$: Observable<string | null> = this.tokenSubject.asObservable();

  constructor(private analytics: AnalyticsService) {
    this.app = initializeApp(firebaseConfig);

    this.auth = getAuth(this.app);
    this.auth.useDeviceLanguage();
    this.auth.setPersistence(browserLocalPersistence);

    // resolveTokenAndPublish() opens authInitialized$ itself, so every publisher settles auth —
    // this callback and the explicit login paths alike.
    this.auth.onAuthStateChanged(async user => {
      await this.resolveTokenAndPublish(user);
    });
  }

  // Monotonic guard for the async token resolution below: transitions that start later
  // always win, even if their getIdToken() resolves first.
  private authPublishSequence = 0;

  // token$ must hold the matching token BEFORE user$ announces an identity: consumers fetch
  // /users/details on user$ emissions and the interceptor stamps token$'s current value —
  // publishing the user first sent the new account's first requests with the previous
  // account's token on a direct A→B switch. The sequence check keeps interleaved callbacks
  // in order too: a sign-out arriving while an older callback awaits getIdToken() must not
  // be overwritten by that callback's late resolution.
  //
  // Whichever transition wins also opens authInitialized$, so auth is settled by whoever
  // actually established the identity. Leaving that to the onAuthStateChanged callback alone
  // deadlocked the app: the explicit login paths call this too — the magic link at
  // /authentication/link is public and signs in during boot — and when one of them won the
  // race against a still-pending restore, the callback saw it had lost and refused to open
  // the gate while the winner had no way to. Guards and every intercepted request then waited
  // forever. Superseded transitions still return above, so the gate never opens on nulls.
  //
  // Returns whether this transition won and published.
  private async resolveTokenAndPublish(user: FirebaseUser | null): Promise<boolean> {
    const sequence = ++this.authPublishSequence;
    let token: string | null = null;

    if (user) {
      try {
        // Bounded so a token fetch that never settles cannot stall initialization — a rejection
        // and a timeout are handled the same way, by publishing the identity without a token.
        token = await firstValueFrom(
          from(user.getIdToken()).pipe(timeout({ each: INITIAL_TOKEN_TIMEOUT_MS }))
        );
      } catch (error) {
        console.error('Failed to get the id token:', error);
      }
    }

    if (sequence !== this.authPublishSequence) {
      return false;
    }

    this.tokenSubject.next(token);
    this.userSubject.next(user);

    // Guarded so the gate still emits exactly once, whoever gets here first.
    if (!this.authInitializedSubject.value) {
      this.authInitializedSubject.next(true);
    }

    return true;
  }

  login(type: AuthProviderType) {
    const provider = this.getProvider(type);

    return from(signInWithPopup(this.auth, provider)).pipe(
      map(result => result.user),
      mergeMap(user => from(this.resolveTokenAndPublish(user)).pipe(map(() => user))),
      tap(user => {
        if (user) {
          this.analytics.track('user_signed_in', { provider: type });
        }
      }),
      catchError(error => {
        if (error.code === 'auth/account-exists-with-different-credential') {
          return this.handleExistingAccountError(error.customData.email, provider, type);
        }

        throw error;
      })
    );
  }

  private handleExistingAccountError(
    email: string,
    provider: AuthProvider,
    type: AuthProviderType
  ) {
    return from(fetchSignInMethodsForEmail(this.auth, email)).pipe(
      map(providers => this.getProvider(providers[0] as AuthProviderType)),
      mergeMap(firstProvider => signInWithPopup(this.auth, firstProvider)),
      mergeMap(result => linkWithPopup(result.user, provider)),
      map(linkResult => linkResult.user),
      mergeMap(user => from(this.resolveTokenAndPublish(user)).pipe(map(() => user))),
      tap(() => {
        this.analytics.track('user_signed_in', { provider: type });
      })
    );
  }

  loginWithToken(token: string) {
    return from(signInWithCustomToken(this.auth, token)).pipe(
      map(result => result.user),
      // Publish before emitting, like the popup paths: callers navigate on this emission,
      // and a guarded destination (magic link with ?returnUrl=/account) would be rejected
      // if user$ were still null when the guard runs.
      mergeMap(user => from(this.resolveTokenAndPublish(user)).pipe(map(() => user))),
      tap(user => {
        if (user) {
          this.analytics.track('user_signed_in', { provider: 'email' });
        }
      }),
      catchError(error => {
        if (error.code === 'auth/account-exists-with-different-credential') {
          return from(fetchSignInMethodsForEmail(this.auth, error.customData.email)).pipe(
            mergeMap(providers => this.login(providers[0] as AuthProviderType))
          );
        }

        throw error;
      })
    );
  }

  signUpWithEmail(email: string) {
    const defaultPassword = this.generateDefaultPassword();
    const adjustedEmail = environment.production ? email : email.replace('@', '+staging@');

    return from(createUserWithEmailAndPassword(this.auth, adjustedEmail, defaultPassword)).pipe(
      map(result => result.user),
      tap(user => {
        // user.accessToken
      })
    );
  }

  logout() {
    return from(signOut(this.auth)).pipe(
      tap(() => {
        // Bump the sequence so an in-flight getIdToken() resolution from before the
        // sign-out can't republish the old identity, then clear token before user.
        this.authPublishSequence++;
        this.tokenSubject.next(null);
        this.userSubject.next(null);
      })
    );
  }

  // Forces a fresh ID token, publishes it on token$ and hands it back so the caller can retry
  // with it. Emits null when there is nobody signed in or the refresh failed — callers decide
  // what to do with that; this never throws.
  //
  // Must be subscribed to. The previous implementation built the same chain with pipe() and
  // dropped it on the floor, so no token was ever produced and the interceptor's 401 retry
  // waited forever for an emission that could not arrive.
  updateToken(): Observable<string | null> {
    return this.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          return of(null);
        }

        // Same guard resolveTokenAndPublish and logout() use: any identity transition that
        // lands while this refresh is in flight bumps the sequence and publishes its own
        // token, so publishing here would stamp the previous account's token over it — the
        // A→B switch case, where checking "is anyone signed in" would not be enough.
        const sequence = this.authPublishSequence;

        // Force refresh: a 401 means the cached token was rejected, and a plain getIdToken()
        // reuses that same token until it expires.
        return from(user.getIdToken(true)).pipe(
          map(token => (token && sequence === this.authPublishSequence ? token : null)),
          tap(token => {
            if (token) {
              this.tokenSubject.next(token);
            }
          })
        );
      }),
      catchError(error => {
        console.error('Failed to refresh the id token:', error);
        return of(null);
      })
    );
  }

  private getProvider(
    type: AuthProviderType | 'google.com' | 'facebook.com' | 'apple.com'
  ): AuthProvider {
    let provider;

    switch (type) {
      case 'apple.com':
      case 'apple':
        provider = new OAuthProvider('apple.com');
        provider.addScope('name');
        provider.addScope('email');
        break;
      case 'facebook.com':
      case 'facebook':
        provider = new FacebookAuthProvider();
        provider.addScope('email');
        provider.addScope('public_profile');
        break;
      case 'google.com':
      case 'google':
        provider = new GoogleAuthProvider();
        provider.addScope('https://www.googleapis.com/auth/user.emails.read'); //https://developers.google.com/identity/protocols/oauth2/scopes#people
        break;
    }

    return provider;
  }

  private generateDefaultPassword(): string {
    // example: 69d3b5abc
    return crypto.randomUUID().toString().substring(0, 10).replace('-', '');
  }
}
